import type { BalanceData } from "@highmarch/domain";
import { saveSections } from "@highmarch/app";
import type { GuideCreateOptions, GuideTopicId } from "./guide";
import { features } from "../../features/registry";
import { featureStepsByTopic } from "./scripts/features";

/**
 * The Guide sheet's topics (⚙ → Guide, DESIGN §3.6 and §6.6): what each is called, where it sits, the lines the
 * captain replays for it (step copy plus the §6.6 extras, as static text: a replay has no live facts), its picture
 * and when it becomes available. Pure data, no DOM: guideSheet.ts draws the rows and the controller's replayTopic
 * plays `topicLines`. Features add their own topics through the feature registry (`allGuideTopics` lists both).
 */

export type TopicGroup = "basics" | "enemies" | "towers" | "castle";

/** A replay card's picture: a unit or tower portrait (portraits.ts portraitUrl id), else a kit icon. */
export interface TopicArt { readonly portrait?: string; readonly icon: string }

export interface GuideTopic {
  readonly id: GuideTopicId;
  readonly group: TopicGroup;
  readonly label: string;
  readonly art: TopicArt;
  /** 1–3 lines, each at most 2 sentences and 22 words (`**x**` marks bold). */
  readonly lines: readonly string[];
  /** The steps that teach it: available once any is done, ✓ once all are. */
  readonly steps: readonly string[];
  /** Available once this level is reachable (highest cleared + 1)… */
  readonly fromLevel?: number;
  /** …or once any of these enemies is (its unlockLevel reachable): the enemy rows say "Not met yet" until then. */
  readonly enemies?: readonly string[];
  /** …or once this tower unlocks (its unlockLevel reachable, balance): the TOWERS_V2 topics. */
  readonly tower?: string;
  /**
   * A topic for a feature another package ships (WP7 achievements, WP8 skins): listed only while the feature is in
   * the build, and only until the feature registers its own topic with the same id (that one wins).
   */
  readonly present?: () => boolean;
}

/** The level the Guide's Practice battle replays, and the first one every basic topic applies to. */
export const PRACTICE_LEVEL = 1;
/** Soldiers and the map controls are first taught on level 2 (levelHints `level ≥ 2`). */
const SECOND_LEVEL = 2;
/** Gear, Chests and builders open with the first clear (unlockCards HUB_UNLOCK_LEVEL), so from level 2 on. */
const CASTLE_FROM = 2;
/** The enemy card and the whole map are first pointed out on level 3 (scripts/features.ts ScoutingFromLevel). */
const SCOUTING_FROM = 3;
/** The Ascend card comes from chapter 2 on (branches.json ascend.minLevel; ascendUi.test pins it). */
export const ASCEND_FROM = 11;
/** Tribute fills once level 2 is cleared (meta.json tribute.unlockAfterLevel; guideTopics.test pins it). */
export const TRIBUTE_FROM = 3;

export const GROUP_TITLES: Readonly<Record<TopicGroup, string>> = { basics: "Battle basics", enemies: "Raiders", towers: "Special towers", castle: "Your fortress" };

export const GUIDE_TOPICS: readonly GuideTopic[] = [
  { id: "roads", group: "basics", label: "Roads", art: { icon: "map" }, fromLevel: PRACTICE_LEVEL,
    steps: ["l1.pickRoad", "l1.placeRoad", "l1.moreRoad"],
    lines: [
      "First, we need a road. Tap a **road card**, then **PLACE** to lay it.",
      "The longer the road, the longer the horde marches under our fire.",
      "Each road card adds a stretch of road. Your road budget shows the tiles left.",
    ] },
  { id: "towers", group: "basics", label: "Towers", art: { portrait: "archer", icon: "tower" }, fromLevel: PRACTICE_LEVEL,
    steps: ["l1.pickTower", "l1.chooseCell", "l1.placeTower", "l1.towerTap"],
    lines: [
      "Tap a **tower card**, then a **green square** by the road.",
      "The circle is its reach. Tap **PLACE** to raise it.",
      "Tap any tower to see how it's faring.",
    ] },
  { id: "points", group: "basics", label: "Points & cards", art: { icon: "coin" }, fromLevel: PRACTICE_LEVEL,
    steps: ["l1.points", "l1.pickOffer", "hint.reroll", "hint.skip"],
    lines: [
      "Every raider we fell earns points. Fill the bar and you get **new cards**.",
      "Tower and upgrade cards cost points toward your next hand. The card shows how many.",
      "Bad hand? **REROLL** deals fresh cards. You earn one every 5 waves.",
    ] },
  { id: "upgrades", group: "basics", label: "Upgrades", art: { icon: "bolt" }, fromLevel: PRACTICE_LEVEL,
    steps: ["hint.upgradeCard", "hint.moveCard"],
    lines: ["An **upgrade card** raises one tower a tier. Tap it, then the tower.",
      "Once three towers stand, a **move card** may replace a road card. Tap it, a tower, then a new square."] },
  { id: "speed", group: "basics", label: "Speed & pause", art: { icon: "fast" }, fromLevel: PRACTICE_LEVEL,
    steps: ["l1.speed", "l1.break"],
    lines: [
      "Too slow for you? Tap **×1** to quicken the pace.",
      "**❚❚** pauses the battle any time. Tap it again to carry on.",
      "**NEXT WAVE**, or **CALL +N** mid-wave, brings the next wave early. They overlap, and you score bonus points.",
    ] },
  { id: "soldiers", group: "basics", label: "Soldiers", art: { portrait: "troop", icon: "flag" }, fromLevel: SECOND_LEVEL,
    steps: ["hint.rally", "hint.lastStand"],
    lines: [
      "Your soldiers guard the road by the fortress. Tap the **flag**, then the road, to move them.",
      "Fortress in danger? Send the soldiers to block the road.",
    ] },
  { id: "stars", group: "basics", label: "Stars", art: { icon: "star" }, fromLevel: PRACTICE_LEVEL,
    steps: ["hub.stars"],
    lines: [
      "Every level has **3 stars**. The battle card shows what each one asks.",
      "Win, keep the fortress healthy, and meet the level's challenge for all **3 stars**.",
    ] },
  { id: "map", group: "basics", label: "Moving the map", art: { icon: "turn_left" }, fromLevel: SECOND_LEVEL,
    steps: ["hint.mapControls", "hint.wholeMap"],
    lines: [
      "Drag to look around, pinch to zoom, and use the arrows to turn the map.",
      "Lost the road? Tap the **map** button or double-tap the ground to see the whole field.",
      "Arrows at the screen's edge point to raiders you can't see yet.",
    ] },
  { id: "undo", group: "basics", label: "Undo", art: { icon: "back" }, fromLevel: SECOND_LEVEL,
    steps: ["hint.undo"],
    lines: [
      "Misplaced a card? Tap **UNDO** to take it back.",
      "UNDO works until the first wave. After that, every card stays where it lands.",
    ] },
  { id: "checkpoint", group: "basics", label: "Checkpoints", art: { icon: "flag" }, fromLevel: SECOND_LEVEL,
    steps: ["end.checkpoint"],
    lines: [
      "Lost a long battle? **RETRY FROM WAVE** picks up at the last checkpoint, not the very start.",
      "Checkpoints come every few waves and just before the boss. The retry is free, once per battle.",
    ] },
  { id: "settings", group: "basics", label: "Comfort settings", art: { icon: "gear" }, fromLevel: PRACTICE_LEVEL,
    steps: ["hub.settings"],
    lines: [
      "**⚙ → Settings** has **Left-handed** controls, **Larger text**, music and effects volume, and haptics.",
      "**Reduced effects** calms a busy battlefield. You can silence my tips there too.",
    ] },

  { id: "flyers", group: "enemies", label: "Flyers", art: { portrait: "flyer", icon: "bolt" }, enemies: ["flyer"],
    steps: ["hint.flyers"],
    lines: ["Flyers skip the road and come straight in. Towers marked **AIR** on their card can hit them."] },
  { id: "shieldbearers", group: "enemies", label: "Shieldbearers", art: { portrait: "shieldbearer", icon: "shield" }, enemies: ["shieldbearer"],
    steps: ["hint.enemy.shieldbearer"],
    lines: ["**Shieldbearers** guard the raiders around them. Take them down first."] },
  { id: "healers", group: "enemies", label: "Healers", art: { portrait: "healer", icon: "heart" }, enemies: ["healer"],
    steps: ["hint.enemy.healer"],
    lines: ["**Healers** patch up nearby raiders. Knock them out first."] },
  { id: "bombers", group: "enemies", label: "Bombers", art: { portrait: "bomber", icon: "flame" }, enemies: ["bomber"],
    steps: ["hint.enemy.bomber"],
    lines: ["**Bombers** fly over and knock towers out for a few seconds. Spread your towers out."] },
  // M21 (docs/ENEMIES_V2.md): each opens with its first sighting (levelHints NEW_ENEMIES)
  { id: "summoners", group: "enemies", label: "Summoners & imps", art: { portrait: "summoner", icon: "star" }, enemies: ["summoner"],
    steps: ["hint.enemy.summoner"],
    lines: [
      "A **Summoner** stops every few seconds in a violet circle and calls up to three **imps**.",
      "Hit it hard while it casts. The imps are tiny and quick: **Cannons** and **Lightning** sweep them up.",
    ] },
  { id: "burrowers", group: "enemies", label: "Burrowers", art: { portrait: "burrower", icon: "target" }, enemies: ["burrower"],
    steps: ["hint.enemy.burrower"],
    lines: [
      "When a tower hits a **Burrower**, it digs in and travels under the road. Nothing can shoot it there.",
      "**Barracks soldiers** hold it so it can't dig. Long-range towers make it waste its two dives early.",
    ] },
  { id: "spellwards", group: "enemies", label: "Spellwards", art: { portrait: "spellward", icon: "crystal" }, enemies: ["spellward"],
    steps: ["hint.enemy.spellward"],
    lines: [
      "A **Spellward**'s rune shield turns aside most magic: Frost, Lightning, Poison and the Arcane Spire barely hurt it.",
      "**Archers, Cannons, Ballistas, Mortars** and Barracks hit it in full. Fire burns it too.",
    ] },
  { id: "stunners", group: "enemies", label: "Stunners", art: { portrait: "stunner", icon: "bolt" }, enemies: ["stunner"],
    steps: ["hint.enemy.stunner"],
    lines: [
      "A **Stunner** stops beside your towers and stomps. Every tower inside its **orange ring** is stunned for 2 seconds.",
      "Hit it from range before it gets close, or kill it while it winds up.",
    ] },
  { id: "bosses", group: "enemies", label: "Bosses", art: { portrait: "boss_warlord", icon: "skull" }, fromLevel: PRACTICE_LEVEL,
    steps: ["hint.firstBoss"],
    lines: ["A **boss** comes at the end of a level. Big and tough: keep every tower in range on it."] },
  { id: "enemies", group: "enemies", label: "Runners & brutes", art: { portrait: "runner", icon: "sword" }, enemies: ["runner", "brute"],
    steps: [],
    lines: ["**Runners** are quick. **Brutes** wear armour, so hit them hard."] },
  { id: "scouting", group: "enemies", label: "Scouting & elites", art: { icon: "target" }, fromLevel: SCOUTING_FROM,
    steps: ["hint.enemyInfo", "hint.elite"],
    lines: [
      "Tap any raider to see what it is, how tough it is, and which towers it's **weak to**.",
      "**Elites** carry a twist, like Warded or Fireproof. Tap one and its card tells you what beats it.",
    ] },

  // TOWERS_V2: the towers that do not simply shoot. Each opens when it unlocks (balance), taught by its card's hint in
  // battle (levelHints NEW_TOWERS) and, for the Mortar, the blind-ring tip when it is placed.
  { id: "barracks", group: "towers", label: "Barracks", art: { portrait: "barracks", icon: "shield" }, tower: "barracks",
    steps: ["hint.newTower.barracks"],
    lines: [
      "The **Barracks** sends three soldiers to hold the road nearest it. Raiders must stop and fight them.",
      "A fallen soldier marches out again ten seconds later. Upgrades give them more health and armour.",
    ] },
  { id: "arcane", group: "towers", label: "Arcane Spire", art: { portrait: "arcane", icon: "bolt" }, tower: "arcane",
    steps: ["hint.newTower.arcane"],
    lines: [
      "The **Arcane Spire** holds a beam on one raider. The longer it holds, the harder it hits: up to three times.",
      "Its magic ignores armour, and it hits flyers too. Give it a long stretch of road.",
    ] },
  { id: "mortar", group: "towers", label: "Mortar", art: { portrait: "mortar", icon: "target" }, tower: "mortar",
    steps: ["hint.newTower.mortar", "hint.mortarBlind"],
    lines: [
      "The **Mortar** lobs shells a long way. Each one bursts and hits every raider near where it lands.",
      "It can't hit anything close: the **red ring** shows its blind spot. Build it back from the road.",
    ] },
  { id: "hex", group: "towers", label: "Hex Totem", art: { portrait: "hex", icon: "skull" }, tower: "hex",
    steps: ["hint.newTower.hex"],
    lines: [
      "The **Hex Totem** doesn't shoot. Raiders inside its purple ring take extra damage from everything.",
      "Cursed healers heal half as much. Put it where your strongest towers already hit.",
    ] },
  // M21: tier IV (docs/TOWERS_V2.md "Tier 4"), taught by the Ascend, branch-picker and skill-card hints in battle.
  { id: "ascend", group: "towers", label: "Tier IV: Ascend", art: { portrait: "archer", icon: "star" }, fromLevel: ASCEND_FROM,
    steps: ["hint.ascendCard", "hint.branchPicker", "hint.skillCard"],
    lines: [
      "From level 11, an **Ascend** card comes once a tower is tier 3. Pick one of **two branches** for the battle.",
      "Every tier-3 tower of that kind rises to **tier IV**. Later ones get there with an Upgrade card.",
      "Each branch has **two skills**. They come on later **skill cards** and work on all its towers.",
    ] },
  { id: "gear", group: "castle", label: "Gear", art: { icon: "bag" }, fromLevel: CASTLE_FROM,
    steps: ["hub.equip", "hub.equipBest", "hub.gearGate", "hub.gearGateEquip"],
    lines: [
      "Gear makes your towers stronger. Open the **Armoury**, tap an item and **Equip** it.",
      "Later levels hit much harder, so **gear is a must**. Replay battles for chests full of it.",
      "On the **Frost** tower, **Beacon** and **Hex Totem**, gear stats they can't use count as **range**.",
    ] },
  { id: "currencies", group: "castle", label: "Gold, gems & more", art: { icon: "coin" }, fromLevel: CASTLE_FROM,
    steps: [],
    lines: [
      "**Gold** pays for tower upgrades and Research. **Gems** are rare: they hire builders and buy speed-ups and skins.",
      "**Forge Essence** raises gear; salvage spare gear for it. **Marks** come from selling gear and spend at the Merchant.",
      "**Trophies** come from the hardest weekly fights. Tap any coin up top to see what it buys.",
    ] },
  { id: "chests", group: "castle", label: "Chests", art: { icon: "chest" }, fromLevel: CASTLE_FROM,
    steps: ["end.firstWin", "hub.firstWin"],
    lines: ["Every battle pays a chest. Fight better, win a better one."] },
  { id: "builders", group: "castle", label: "Builders", art: { icon: "builder" }, fromLevel: CASTLE_FROM,
    steps: ["hub.upgrade", "hub.upgradeGo", "hub.upgradeWatch"],
    lines: [
      "Open **Towers** and upgrade one to put our builder to work.",
      "Upgrades take time. The builder works on while you're away.",
    ] },
  { id: "orders", group: "castle", label: "Orders", art: { icon: "trophy" }, fromLevel: CASTLE_FROM,
    steps: ["hub.orders"],
    lines: ["Daily **Orders** pay out chests. Finish all three each day."] },
  { id: "tribute", group: "castle", label: "Tribute", art: { icon: "coin" }, fromLevel: TRIBUTE_FROM,
    steps: ["hub.tribute"],
    lines: [
      "The fortress gathers **Tribute** while you're away. Tap its chip on the fortress to claim it.",
      "It stops filling once full, so claim it once or twice a day.",
    ] },
  { id: "achievements", group: "castle", label: "Achievements", art: { icon: "trophy" }, fromLevel: CASTLE_FROM,
    steps: ["hub.achievements"], present: () => saveSections.has("achievements"),
    lines: [
      "**Achievements** mark your milestones: levels, stars, raiders beaten. Find them in the **⚙ menu → Achievements**.",
      "Each one pays once. Claim it and it's yours for good.",
    ] },
  { id: "skins", group: "castle", label: "Skins", art: { icon: "star" }, fromLevel: CASTLE_FROM,
    steps: ["hub.skins"], present: () => saveSections.has("cosmetics"),
    lines: [
      "**Skins** change how your towers, fortress, troops, flag and road look. Looks only — pure glory.",
      "Earn skins by levelling towers and in events, or buy them with gems or Marks.",
    ] },
];

/**
 * The core topics, then those the features registered (features/registry.ts `guideTopic`). A registered topic
 * replaces a core one with its id (a stand-in for a feature, `present`); every topic also counts the feature hints
 * that teach it (scripts/features.ts), so a WP5/WP6 topic registered with `steps: []` still gets its ✓.
 */
export function allGuideTopics(): readonly GuideTopic[] {
  const registered = features.guideTopics();
  const ids = new Set(registered.map((t) => t.id));
  const hints = featureStepsByTopic();
  const withHints = (t: GuideTopic): GuideTopic => {
    const more = (hints.get(t.id) ?? []).filter((id) => !t.steps.includes(id));
    return more.length === 0 ? t : { ...t, steps: [...t.steps, ...more] };
  };
  const core = GUIDE_TOPICS.filter((t) => !ids.has(t.id) && (t.present?.() ?? true));
  return [...core, ...registered].map(withHints);
}

export function guideTopic(id: GuideTopicId): GuideTopic | undefined { return allGuideTopics().find((t) => t.id === id); }

/** The lines a topic replays (the controller's replayTopic plays them as info cards). */
export function topicLines(id: GuideTopicId): readonly string[] { return guideTopic(id)?.lines ?? []; }

/** The unlock levels createGuide's migration and the topics' availability read, from balance. */
export function guideUnlocks(B: BalanceData): GuideCreateOptions["unlocks"] {
  return {
    enemies: B.enemies.map((e) => ({ id: e.id, unlockLevel: e.unlockLevel, air: e.isAir })),
    towers: B.towers.filter((t) => !t.isBase).map((t) => ({ id: t.id, unlockLevel: t.unlockLevel })),
  };
}

export type TopicState = "done" | "new" | "open" | "locked";

export interface TopicFacts {
  readonly highestCleared: number;
  readonly isDone: (id: string) => boolean;
  readonly unlocks: GuideCreateOptions["unlocks"];
  /** Topics the player has opened in the Guide sheet (their NEW goes). */
  readonly viewed: ReadonlySet<string>;
}

/** The level a topic opens at: its fromLevel, else its tower's unlock or the earliest of its enemies' (never, when none is known). */
export function topicLevel(t: GuideTopic, unlocks: TopicFacts["unlocks"]): number {
  const levels = (t.enemies ?? []).map((id) => unlocks.enemies.find((e) => e.id === id)?.unlockLevel ?? Number.POSITIVE_INFINITY);
  const tower = t.tower !== undefined ? unlocks.towers.find((x) => x.id === t.tower)?.unlockLevel ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;
  return Math.min(t.fromLevel ?? Number.POSITIVE_INFINITY, tower, ...levels);
}

/**
 * A row's state: ✓ once every step that teaches it is done; available once any step is done or its level is
 * reachable (highest cleared + 1); NEW while it is one that opened later than the first battle (just unlocked) and
 * has been neither taught nor opened in the sheet; else locked.
 */
export function topicState(t: GuideTopic, f: TopicFacts): TopicState {
  const done = t.steps.filter((s) => f.isDone(s)).length;
  if (t.steps.length > 0 && done === t.steps.length) return "done";
  const opens = topicLevel(t, f.unlocks);
  if (done === 0 && opens > f.highestCleared + 1) return "locked";
  return done === 0 && opens > PRACTICE_LEVEL && !f.viewed.has(t.id) ? "new" : "open";
}
